import React, { useState, useRef } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import { useHistory } from "react-router-dom";
import { FA } from "components/FA";
import { openDownloadModal } from "components/standalone/Download";
import * as Yup from "yup";
import { useQuery, useDropArea } from "hooks";
import styles from "components/DragOver.module.css";

import { modalService, alertService } from "services";

const FILE_SIZE = 15 * 1024 * 1024;
const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const UploadForm = ({ close, isModal = false, files = null }) => {
  const submitRef = useRef(null);
  const { actions } = useQuery();
  const [droppedFiles, setDroppedFiles] = useState(files);
  const [handleDroparea, state] = useDropArea({
    onFiles: (files) => setDroppedFiles({ target: { files } }),
  });
  const history = useHistory();
  const initialValues = {
    upload: undefined,
  };
  const validationSchema = Yup.object().shape({
    upload: Yup.mixed()
      .test(
        "fileSize",
        "File too large",
        (value) => value && value.size <= FILE_SIZE
      )
      .test(
        "fileFormat",
        "Unsupported Format",
        (value) => value && SUPPORTED_FORMATS.includes(value.type)
      ),
  });

  function onSubmit(fields, { setStatus, setSubmitting }) {
    setStatus();
    const formData = new FormData();
    formData.append("upload", fields.upload, fields.upload.name);
    actions
      .file(formData)
      .then((response) => {
        alertService.success(`File "${response.title}" saved`, {
          keepAfterRouteChange: true,
        });
        if (typeof close === "function") {
          close();
        } else {
          history.push("/");
        }
      })
      .catch((error) => {
        if (
          error ===
          "Uploads are not implemented in this instance of KnowING.App"
        ) {
          if (typeof close === "function") {
            close();
          } else {
            history.push("/");
          }
          openDownloadModal(true);
          return;
        }
        setSubmitting(false);
        alertService.error(error);
      });
  }
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        handleSubmit,
      }) => {
        const handleFileChange = (e) => {
          e.preventDefault && e.preventDefault();
          setFieldValue("upload", e.target.files[0]);
        };
        if (
          droppedFiles != null &&
          values.upload !== droppedFiles.target.files[0]
        ) {
          handleFileChange(droppedFiles);
        }
        if (
          values.upload != null &&
          !isSubmitting &&
          submitRef.current != null
        ) {
          submitRef.current.click();
        }
        return (
          <Form>
            <h3 className="card-header">
              Personal Upload
              {isModal && (
                <FA
                  title="Close"
                  onClick={() => close()}
                  icon="times"
                  size="xs"
                />
              )}
            </h3>
            <div
              className={`card-body${state.over ? ` ${styles.dragover}` : ""}`}
              {...handleDroparea}
            >
              <div className="form-group">
                <div className="custom-file">
                  <input
                    name="upload"
                    type="file"
                    onChange={handleFileChange}
                    id="file-upload"
                    className={
                      "custom-file-input" +
                      (errors.upload && touched.upload ? " is-invalid" : "")
                    }
                  />
                  <label className="custom-file-label" htmlFor="file-upload">
                    {values.upload != null ? values.upload.name : "Choose file"}
                  </label>
                  <ErrorMessage
                    name="upload"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              </div>
              <div className="form-group">
                <button
                  ref={submitRef}
                  type="submit"
                  disabled={values.upload == null || isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting && (
                    <span className="spinner-border spinner-border-sm mr-1"></span>
                  )}
                  Upload
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const openUploadFormModal = (responseId, files = null) =>
  modalService.open({
    id: "upload",
    children: (
      <UploadForm
        isModal={true}
        close={() => modalService.close("upload")}
        files={files}
      />
    ),
  });

export { UploadForm, openUploadFormModal };
